import axios from "@/axios";
import auditLogSignalR from "@/services/audit_log_SignalR";

export const namespaced = true;

// AbortController for cancelling pending requests
let currentAbortController = null;

// Timer for delayed loading spinner (must be outside Vuex state)
let loadingTimerId = null;

export const state = {
  auditLog: [],
  isLoading: false,
  loadingText: "",
  totalAuditLogCount : null,
  totalUniqueActionCount: null,
  currentOrderId: null,
  currentPage: 1,
  pageSize: 10
};

export const getters = {
  getAuditLog: (state) => {
    return state.auditLog;
  },
  getTotalAuditLogCount: (state) => {
    return state.totalAuditLogCount
  },
  getTotalUniqueActionCount: (state) => {
    return state.totalUniqueActionCount
  },
  getCurrentPage: (state) => {
    return state.currentPage
  },
  getPageSize: (state) => {
    return state.pageSize
  },
  auditLogLoadingText: (state) => {
    return state.loadingText;
  },
  isLoading: (state) => {
    return state.isLoading;
  },
  getCurrentOrderId: (state) => {
    return state.currentOrderId;
  },
};

export const mutations = {
  SET_AUDIT_LOG(state, auditLog) {  
    state.auditLog = state.auditLog.concat(auditLog);
  },
  // Replace audit log atomically (clear + set in one mutation to avoid flicker)
  REPLACE_AUDIT_LOG(state, auditLog) {
    state.auditLog = auditLog;
  },
  PREPEND_AUDIT_LOG(state, auditLogs) {
    // Add new logs to the beginning (most recent first)
    state.auditLog = [...auditLogs, ...state.auditLog];
  },
  CLEAR_AUDIT_LOG(state) {
    state.auditLog = [];
    state.loadingText = "";
    state.totalUniqueActionCount = null;
    state.currentPage = 1;
  },
  SET_AUDIT_LOG_LENGTH(state , number){
    state.totalAuditLogCount = number
  },
  SET_TOTAL_UNIQUE_ACTION_COUNT(state, number) {
    state.totalUniqueActionCount = number
  },
  SET_CURRENT_PAGE(state, page) {
    state.currentPage = page
  },
  SET_PAGE_SIZE(state, size) {
    state.pageSize = size
  },
  SET_AUDIT_LOG_LOADING_TEXT(state, text) {
    state.loadingText = text;
  },
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_CURRENT_ORDER_ID(state, orderId) {
    state.currentOrderId = orderId;
  },
};

// Keep track of the callback so we can remove it later
let currentOrderCallback = null;

export const actions = {
  clearData({ commit, state }) {
    // Unsubscribe from current order if subscribed
    if (state.currentOrderId) {
      auditLogSignalR.unsubscribeFromOrder(state.currentOrderId, currentOrderCallback);
      currentOrderCallback = null;
      commit("SET_CURRENT_ORDER_ID", null);
    }
    commit("CLEAR_AUDIT_LOG");
  },
  async fetchAuditLog({ commit }, orderId = 0) {
    if (orderId != 0) {
      await axios
        .get(`/api/orderauditlogs/${orderId}`)
        .then((res) => {
          if (res.data.length > 0) {
            commit("SET_AUDIT_LOG_LOADING_TEXT", "Audit Logs Loaded...");
            commit("SET_AUDIT_LOG", res.data);
          } else {
            commit("SET_AUDIT_LOG_LOADING_TEXT", "No Audit Logs Available!");
          }
        })
        .catch((err) => {
          //console.log(err); //not handelled
        });
    } else {
      commit("CLEAR_AUDIT_LOG");
      //commit("SET_AUDIT_LOG", DummyAuditData);
    }
  },
  async fetchAuditLogPagination({ commit, state, dispatch }, apiInfo) {
    try {
      // Cancel any pending request
      if (currentAbortController) {
        currentAbortController.abort();
      }
      currentAbortController = new AbortController();
      
      // Unsubscribe from previous order if switching orders (non-blocking)
      if (apiInfo.isClear && state.currentOrderId && state.currentOrderId !== apiInfo.orderId) {
        auditLogSignalR.unsubscribeFromOrder(state.currentOrderId, currentOrderCallback);
        currentOrderCallback = null;
      }
      
      // Delay showing loading spinner by 300ms - if response is fast, no spinner flash
      // Note: We don't clear data here - we use REPLACE_AUDIT_LOG for atomic swap when data arrives
      if (loadingTimerId) clearTimeout(loadingTimerId);
      loadingTimerId = setTimeout(() => {
        commit("SET_LOADING", true);
      }, 300);

      // Build URL - only include search parameter if it has a value
      const searchParam = apiInfo.searchText ? `/${encodeURIComponent(apiInfo.searchText)}` : '';
      let res = await axios.get(
        `/api/orderauditlogs/pagination/${apiInfo.orderId}/${apiInfo.pageNumber}/${apiInfo.resultsPerPage}${searchParam}`,
        { signal: currentAbortController.signal }
      )

      // Clear timer - response arrived before delay
      if (loadingTimerId) {
        clearTimeout(loadingTimerId);
        loadingTimerId = null;
      }

      // Clear before setting if requested
      if (apiInfo.isClear) {
        commit("CLEAR_AUDIT_LOG");
      }

      if (res.data.results.length > 0) {
        commit("SET_AUDIT_LOG_LOADING_TEXT", "");
        commit("SET_AUDIT_LOG", res.data.results);
        
        commit("SET_CURRENT_PAGE", apiInfo.pageNumber);
        commit("SET_PAGE_SIZE", apiInfo.resultsPerPage);
        
        // Only update counts from first page (backend skips count query on subsequent pages)
        if (res.data.recordCount > 0) {
          commit("SET_AUDIT_LOG_LENGTH", res.data.recordCount);
        }
        if (res.data.totalUniqueActionCount > 0) {
          commit("SET_TOTAL_UNIQUE_ACTION_COUNT", res.data.totalUniqueActionCount);
        }

        // Subscribe to real-time updates for this order (only on first page, non-blocking)
        if (apiInfo.pageNumber === 1 || apiInfo.isClear) {
          const subscribedOrderId = apiInfo.orderId;
          commit("SET_CURRENT_ORDER_ID", subscribedOrderId);
          
          // Create callback with closure over the correct values
          currentOrderCallback = (data) => {
            console.log('SignalR callback received:', data);
            if (data && data.auditLogs && data.orderId === subscribedOrderId) {
              console.log('Prepending audit logs to state');
              commit("PREPEND_AUDIT_LOG", data.auditLogs);
              // Update total count
              if (state.totalAuditLogCount != null) {
                commit("SET_AUDIT_LOG_LENGTH", state.totalAuditLogCount + data.auditLogs.length);
              }
              if (state.totalUniqueActionCount != null) {
                const uniqueDates = [...new Set(data.auditLogs.map(l => l.logDate))].length;
                commit("SET_TOTAL_UNIQUE_ACTION_COUNT", state.totalUniqueActionCount + uniqueDates);
              }
            }
          };
          
          // Don't await - let SignalR subscribe in background
          auditLogSignalR.subscribeToOrder(subscribedOrderId, currentOrderCallback);
        }
      } else {
        commit("SET_AUDIT_LOG_LOADING_TEXT", "No Audit Logs Available!");
      }
    } catch(err) {
      // Ignore cancelled requests (user started a new search)
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      commit("SET_AUDIT_LOG_LOADING_TEXT", "Error loading audit logs");
    } finally {
      // Clear the loading timer and hide spinner
      if (loadingTimerId) {
        clearTimeout(loadingTimerId);
        loadingTimerId = null;
      }
      commit("SET_LOADING", false);
    }
  },
};
